import React, { useMemo, useState, useCallback, memo } from 'react'
import { TbCheckbox } from 'react-icons/tb'
import { FiMail } from 'react-icons/fi'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { getMyEvents } from '_redux/modules/event'
import { formatDay, getPreciseTimeLeft } from '_utils/function'
import { useModal } from '_hooks/useModal'
import {
  BUYER_ORDER_UNPAID,
} from '_utils/constant'
import AddPackingListModal from '_pages/Event/components/AddPackingListModal'
import ListOrderUnpaidModal from './ListOrderUnpaidModal'
import { formatCurrency } from '../helper/formatNumber'
import useDashboardRedirect from '../hooks/useDashboardRedirect'

const SKELETON_ITEMS = [1, 2, 3]
const AVATAR_COLORS = ['#E24B4A', '#009A97', '#E8971A', '#2F80ED', '#9B51E0', '#EB5757']

const getBadgeInfo = (days) => {
  if (days <= 3) return { dotColor: '#E24B4A', badgeClass: 'red' }
  if (days <= 7) return { dotColor: '#E8971A', badgeClass: 'amber' }
  return { dotColor: '#009A97', badgeClass: 'teal' }
}

const formatTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp * 1000)
  let hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  return `${hours}:${minutes} ${ampm}`
}

function EventsReport({ dataEvents, data, loadingEvents, isViewAll, filters, eventTimeRange }) {
  const history = useHistory()
  const dispatch = useDispatch()
  const { redirectToManageOrders } = useDashboardRedirect()

  const [isOpenAddPackingList, setIsOpenAddPackingList] = useState(false)
  const listOrderUnpaidModal = useModal()
  const [selectedBuyer, setSelectedBuyer] = useState(null)

  const handleOpenUnpaidModal = useCallback(
    (item) => {
      if (item) {
        setSelectedBuyer(item)
      }
      listOrderUnpaidModal.openModal()
    },
    [listOrderUnpaidModal]
  )

  const togglePackingList = useCallback(() => {
    if (!isOpenAddPackingList) dispatch(getMyEvents)
    setIsOpenAddPackingList((prev) => !prev)
  }, [isOpenAddPackingList, dispatch])

  const debtorList = useMemo(
    () =>
      (data?.debtorReport || []).map((debtor, idx) => ({
        uid: debtor.uid || idx,
        phone: debtor.phone,
        photoUrl: debtor.photoUrl,
        bName: debtor.bName || 'Unknown Buyer',
        totalUnpaidOrders: debtor.totalUnpaidOrders || 0,
        totalUnpaidAmount: debtor.totalUnpaidAmount || 0,
        unpaidOrders: debtor.unpaidOrders || [],
        initials: debtor.bName ? debtor.bName.trim().charAt(0).toUpperCase() : 'U',
        avatarBg: AVATAR_COLORS[idx % AVATAR_COLORS.length],
      })),
    [data?.debtorReport]
  )

  const totalOutstanding = useMemo(
    () => debtorList.reduce((sum, item) => sum + (item.totalUnpaidAmount || 0), 0),
    [debtorList]
  )

  const collectionEvents = useMemo(
    () =>
      (dataEvents?.upcommingEvent || []).map((ev) => {
        const { label: daysIn, days } = getPreciseTimeLeft(ev.pickupTimestamp)
        const { dotColor, badgeClass } = getBadgeInfo(days)
        return {
          eId: ev.eId,
          name: ev.eName || 'Unnamed Event',
          dotColor,
          orders: ev.totalOrder || 0,
          location: ev.hostName || 'No Host',
          date: ev.pickupTimestamp ? formatDay(ev.pickupTimestamp) : '',
          time: formatTime(ev.pickupTimestamp),
          daysIn,
          badgeClass,
        }
      }),
    [dataEvents?.upcommingEvent]
  )

  const handleNavigation = useCallback(
    (eventId) => {
      if (!isViewAll) history.push(`/event/manage-orders?eventId=${eventId}`)
    },
    [history, isViewAll]
  )

  const handleNavigationManageOrder = useCallback(() => {
    if (isViewAll) return
    redirectToManageOrders({
      dateRangeOrder: { start: filters?.start, end: filters?.end },
      paymentStatus: [
        { value: BUYER_ORDER_UNPAID, label: 'Not Paid' },
      ],
      dateRangeEvent: eventTimeRange,
      eventId: filters?.eid,
      status: filters?.status,
    })
  }, [isViewAll, redirectToManageOrders, filters, eventTimeRange])

  if (loadingEvents) {
    return (
      <div className='events-report-section'>
        <div className='section-label'>Events</div>
        <div className='bottom-grid'>
          {[1, 2].map((cardKey) => (
            <div key={cardKey} className='panel-card'>
              <div className='panel-header'>
                <div>
                  <div className={`skeleton-loader skeleton-header-title ${cardKey === 2 ? 'upcoming' : ''}`} />
                  <div className='skeleton-loader skeleton-header-desc' />
                </div>
              </div>
              <div className='panel-body-loading'>
                {SKELETON_ITEMS.map((i) => (
                  <div key={i} className='skeleton-row'>
                    <div className='skeleton-loader skeleton-dot' />
                    <div className='skeleton-info'>
                      <div className='skeleton-loader skeleton-text-main' />
                      <div className='skeleton-loader skeleton-text-sub' />
                    </div>
                    <div className='skeleton-loader skeleton-badge' />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className='events-report-section'>
      <div className='section-label'>Events</div>
      <div className='bottom-grid'>
        {/* Card 1: Payment Reminders */}
        <div className='panel-card payment-reminders-card'>
          <div className='panel-header'>
            <div>
              <div className='panel-title'>Payment Reminders</div>
              <div className='panel-description'>Buyers with outstanding balances, up to date</div>
            </div>
            <span
              onClick={isViewAll ? undefined : handleNavigationManageOrder}
              className={`panel-link ${isViewAll ? 'disabled-link' : ''}`}
            >
              View all &rarr;
            </span>
          </div>

          <div className='payment-grid-header'>
            <div className='payment-header-label col-buyer'>BUYER</div>
            <div className='payment-header-label col-owes'>OWES</div>
            <div className='payment-header-label col-days'>ORDERS</div>
            <div className='payment-header-label col-action'>ACTION</div>
          </div>

          <div className='debtor-list'>
            {debtorList.length === 0 ? (
              <div className='empty-state'>No buyers with outstanding balances</div>
            ) : (
              debtorList.map((item, idx) => (
                <div key={item.uid || idx} className='debtor-row'>
                  <div className='buyer-cell'>
                    {item.photoUrl ? (
                      <img src={item.photoUrl} alt={item.bName} className='buyer-avatar-img' />
                    ) : (
                      <div className='buyer-avatar-initial' style={{ background: item.avatarBg }}>
                        {item.initials}
                      </div>
                    )}
                    <span className='buyer-name'>{item.bName}</span>
                  </div>
                  <div className='owes-cell'>{formatCurrency(item.totalUnpaidAmount)}</div>
                  <div className='days-cell'>{item.totalUnpaidOrders}</div>
                  <div className='action-cell'>
                    <button
                      className='btn-remind'
                      onClick={() => handleOpenUnpaidModal(item)}
                    >
                      <FiMail className='btn-icon' />
                      Remind
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {debtorList.length > 0 && (
            <div className='payment-footer'>
              <div className='total-outstanding'>
                Total outstanding: <span className='total-amount'>{formatCurrency(totalOutstanding)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Card 2: Upcoming Collections */}
        <div className='panel-card'>
          <div className='panel-header'>
            <div>
              <div className='panel-title'>Upcoming Collections</div>
              <div className='panel-description'>Prepare packing for these dates</div>
            </div>
            <span
              onClick={isViewAll ? undefined : () => history.push('/event/list-packing-list')}
              className={`panel-link ${isViewAll ? 'disabled-link' : ''}`}
            >
              Packing lists &rarr;
            </span>
          </div>

          <div className='grid-header'>
            <div className='grid-header-label'>Event</div>
            <div className='grid-header-label col-collection'>Collection</div>
            <div className='grid-header-label col-in'>In</div>
          </div>

          <div className='event-list'>
            {collectionEvents.length === 0 ? (
              <div className='empty-state'>No upcoming collections</div>
            ) : (
              collectionEvents.slice(0, 5).map((ev, idx) => (
                <div key={ev.eId || idx} className='event-row'>
                  <div className='event-dot' style={{ background: ev.dotColor }} />
                  <div
                    className={`event-info ${isViewAll ? 'disabled-event' : ''}`}
                    onClick={() => handleNavigation(ev.eId)}
                  >
                    <div className='event-name'>{ev.name}</div>
                    <div className='event-meta'>
                      {ev.orders} orders &middot; {ev.location}
                    </div>
                  </div>
                  <div className='event-collection-info'>
                    <div className='event-collection-date'>{ev.date}</div>
                    <div className='event-collection-time'>{ev.time}</div>
                  </div>
                  <span className={`badge ${ev.badgeClass}`}>{ev.daysIn}</span>
                </div>
              ))
            )}
          </div>

          {!isViewAll && (
            <div className='packing-cta'>
              <button className='button-start-packing' onClick={togglePackingList}>
                <TbCheckbox size={14} color='#009A97' />
                Start Packing List
              </button>
            </div>
          )}
        </div>
      </div>

      {isOpenAddPackingList && (
        <AddPackingListModal modal={isOpenAddPackingList} toggle={togglePackingList} />
      )}

      <ListOrderUnpaidModal
        isOpen={listOrderUnpaidModal.isOpen}
        toggle={listOrderUnpaidModal.closeModal}
        closeModal={listOrderUnpaidModal.closeModal}
        buyer={selectedBuyer || undefined}
      />
    </div>
  )
}

export default memo(EventsReport)
